

import React, { useState, useEffect } from 'react';
import { FiShoppingCart, FiShield, FiChevronRight } from 'react-icons/fi';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useAuth } from '../context/AuthContext';
import { cartAPI } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://stelonne-market.online/backend/public';

const ProductCard = ({ product, showSupplierInfo = false }) => {
  const [imageUrl, setImageUrl] = useState('/placeholder.jpg');
  const [addingToCart, setAddingToCart] = useState(false);
  const navigate = useNavigate();
  const rootlocation = useLocation();
  const { user, isAdmin, isAuthenticated } = useAuth();

  // Vérification des permissions basée sur user.role.permissions
  const canViewSupplierNames = () => {
    // 1. Si showSupplierInfo est false, on n'affiche pas
    if (!showSupplierInfo) return false;
    
    // 2. Si l'utilisateur n'est PAS connecté, il ne peut PAS voir
    if (!isAuthenticated) return false;
    
    // 3. Si admin, il peut voir
    if (isAdmin) return true;
    
    // 4. Vérifier directement dans les permissions de l'utilisateur
    if (user && user.role && user.role.permissions) {
      // Vérifier si le tableau de permissions contient "view_suppliers"
      const hasViewSuppliers = user.role.permissions.includes('view_suppliers');
      
      return hasViewSuppliers;
    }
    
    // 5. Vérifier si le rôle de l'utilisateur lui donne accès (fallback)
    if (user && user.role) {
      // Si role est un objet (avec name), vérifier role.name
      const roleName = typeof user.role === 'object' ? user.role.name : user.role;
      
      // SEULEMENT les rôles admin et sous_admin peuvent voir
      const allowedRoles = ['admin', 'sous_admin'];
      const canView = allowedRoles.includes(roleName);
      
      return canView;
    }
    
    // 6. Par défaut, refuser l'accès
    return false;
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '0';
    const priceNum = parseFloat(price);
    if (isNaN(priceNum)) return '0';
    
    return priceNum.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatSuppliers = (suppliers) => {
    if (!suppliers || !Array.isArray(suppliers)) return null;
    
    const canView = canViewSupplierNames();
    
    if (!isAuthenticated || user?.role?.name === 'user') {
      // Ne rien afficher du tout si pas de permission
      return null;
    }
    
    const validSuppliers = suppliers.filter(s => s && s.name);
    
    if (validSuppliers.length === 0) {
      return null; // Ne rien afficher si pas de fournisseurs
    }
    
    // MODIFIÉ : Adapté au nouveau thème jaune/grise
    return (
      <div className="text-xs mb-1">
        <div className="flex items-center gap-1 text-yellow-400">
          <FiShield className="text-yellow-400 text-xs" />
          <span className="font-medium">Fournisseurs: {validSuppliers.length}</span>
        </div>
      </div>
    );
  };

  const addToLocalCart = (item) => {
    try {
      const currentCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      const existingIndex = currentCart.findIndex(cartItem => cartItem.product_id === item.product_id);
      
      if (existingIndex >= 0) {
        currentCart[existingIndex].quantity += item.quantity;
        
        if (currentCart[existingIndex].quantity > currentCart[existingIndex].max_quantity) {
          currentCart[existingIndex].quantity = currentCart[existingIndex].max_quantity;
          alert(`Quantité maximale atteinte. Stock: ${currentCart[existingIndex].max_quantity}`);
        }
      } else {
        currentCart.push(item);
      }
      
      localStorage.setItem('guest_cart', JSON.stringify(currentCart));
      updateCartBadge(currentCart.reduce((total, item) => total + item.quantity, 0));
      
      return { success: true, message: 'Produit ajouté au panier local' };
    } catch (error) {
      console.error('Erreur lors de l\'ajout au panier local:', error);
      return { success: false, message: 'Erreur lors de l\'ajout au panier' };
    }
  };

  const addToBackendCart = async (item) => {
    try {
      setAddingToCart(true);
      
      if (!user || !user.id) {
        return { 
          success: false, 
          message: 'Utilisateur non identifié. Veuillez vous reconnecter.' 
        };
      }
      
      const cartData = {
        user_id: user.id,
        product_id: item.product_id,
        quantity: item.quantity,
      };
      
      const response = await cartAPI.addToCart(cartData);
      
      if (response.success) {
        if (response.data && response.data.total_items) {
          updateCartBadge(response.data.total_items);
        }
        
        return { 
          success: true, 
          message: 'Produit ajouté au panier',
          cartData: response.data
        };
      } else {
        return { 
          success: false, 
          message: response.message || 'Erreur lors de l\'ajout au panier' 
        };
      }
    } catch (error) {
      console.error('Erreur API panier:', error);
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            return { 
              success: false, 
              message: 'Session expirée. Veuillez vous reconnecter.' 
            };
          case 422:
            return { 
              success: false, 
              message: error.response.data.message || 'Données invalides' 
            };
          default:
            return { 
              success: false, 
              message: 'Erreur de connexion au serveur' 
            };
        }
      }
      
      return { 
        success: false, 
        message: 'Erreur de connexion au serveur' 
      };
    } finally {
      setAddingToCart(false);
    }
  };

  const updateCartBadge = (itemCount) => {
    const badgeElement = document.querySelector('.cart-badge');
    if (badgeElement) {
      badgeElement.textContent = itemCount > 99 ? '99+' : itemCount;
      badgeElement.classList.toggle('hidden', itemCount === 0);
    }
  };

  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    
    if (imageName.startsWith('http') || imageName.startsWith('blob:')) {
      return imageName;
    }
    
    return `${API_BASE_URL}/storage/products/${imageName}`;
  };

  const getPrimaryImage = (images) => {
    if (!images || images.length === 0) return null;
    return images;
  };

  const primaryImage = getPrimaryImage(product.image);
  const imageUrlSoft = primaryImage ? getImageUrl(primaryImage.image) : null;

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    
    const stockQuantity = product.quantity || 10;
    if (stockQuantity === 0) {
      alert('❌ Ce produit est en rupture de stock !');
      return;
    }
    
    const cartItem = {
      product_id: product.id,
      name: product.name,
      price: product.price,
      image: primaryImage?.image,
      quantity: 1,
      max_quantity: stockQuantity,
    };
    
    if (isAuthenticated && user) {
      const result = await addToBackendCart(cartItem);
      
      if (result.success) {
        console.log(`✅ ${product.name} ajouté au panier !`);
      } else {
        alert(`❌ ${result.message}`);
        
        if (result.message.includes('Session expirée') || result.message.includes('reconnecter')) {
          setTimeout(() => {
            navigate('/login', { 
              state: { 
                from: window.location.pathname,
                message: 'Session expirée. Veuillez vous reconnecter.'
              } 
            });
          }, 1500);
        }
      }
    } else {
      const result = addToLocalCart(cartItem);
      
      if (result.success) {
        console.log(`✅ ${product.name} ajouté au panier local !`);
      } else {
        alert(`❌ ${result.message}`);
      }
    }
  };

  // const handleViewDetails = () => {
  //   navigate(`/product/${product.id}`);
  // };

  // useEffect(() => {
  //   fetchProductImage();
  // }, [product.id]);

  // const fetchProductImage = async () => {
  //   try {
  //     setImageUrl('https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=Produit');
  //   } catch (error) {
  //     console.error('Erreur image:', error);
  //     setImageUrl('https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=Produit');
  //   }
  // };

  const stockQuantity = product.quantity || 10;
  const getStockStatus = () => {
    if (stockQuantity > 10) {
      return { text: `En Stock`, color: 'bg-gray-700 text-yellow-400' };
    } else if (stockQuantity > 0) {
      return { text: `Limité`, color: 'bg-yellow-100 text-yellow-800' };
    } else {
      return { text: 'Rupture', color: 'bg-green-100 text-green-800' };
    }
  };

  const stockStatus = getStockStatus();

  // Extrait un aperçu de la description (premières 60 caractères)
  const getDescriptionPreview = () => {
    if (!product.description) return 'Aucune description disponible';
    
    const preview = product.description.substring(0, 60);
    if (product.description.length > 60) {
      return preview + '...';
    }
    return preview;
  };

  // Ta fonction handleViewDetails modifiée
  const handleViewDetails = () => {
    const currentUrl = rootlocation.pathname + rootlocation.search;
    console.log('🔗 Navigation depuis:', currentUrl);
    navigate(`/product/${product.id}`, {
        state: { from: currentUrl }
    });
  };

  return (
    <div onClick={handleViewDetails} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group border border-gray-200 flex flex-col h-full w-full">
      {/* Image container - Hauteur FIXE pour uniformité */}
      <div
        className="relative overflow-hidden cursor-pointer flex-shrink-0 block"
        // onClick={handleViewDetails}
      >
        <LazyLoadImage
          src={imageUrlSoft || imageUrl}
          alt={product.name}
          // effect="blur"
          className="w-full h-32 sm:h-36 object-cover group-hover:scale-105 transition-transform duration-200"
          style={{ aspectRatio: '4/3' }}
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=Produit';
          }}

          threshold={100}  // ← Charge l'image 100px avant qu'elle n'apparaisse
        />
    
        {/* Badge de stock - MODIFIÉ : Adapté au nouveau thème */}
        <div className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold ${stockStatus.color} border border-gray-400`}>
          {stockStatus.text}
        </div>
      </div>

      {/* Contenu - COMPACT */}
      <div className="p-2 sm:p-3 flex flex-col flex-grow">
        {/* Nom du produit - Ligne unique */}
        <h3 
          className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-1 mb-1 cursor-pointer hover:text-yellow-400"
          // onClick={handleViewDetails}
          // title={product.name}
        >
          {product.name}
        </h3>

        {/* Aperçu description - Toujours visible */}
        <div className="mb-1">
          <p className="text-[10px] sm:text-xs text-gray-500 line-clamp-2">
            {getDescriptionPreview()}
          </p>
          <div
            className="text-[10px] text-yellow-400 hover:text-yellow-500 font-medium mt-0.5 flex items-center gap-0.5"
          >
            Voir détails <FiChevronRight className="text-[9px]" />
          </div>
          {/* <button 
            onClick={handleViewDetails}
            className="text-[10px] text-yellow-400 hover:text-yellow-500 font-medium mt-0.5 flex items-center gap-0.5"
          >
            Voir détails <FiChevronRight className="text-[9px]" />
          </button> */}
        </div>

        {/* Fournisseurs - SEULEMENT si permission */}
        {formatSuppliers(product.suppliers)}

        {/* Prix et actions - COMPACT */}
        <div className="flex items-center justify-between mt-auto pt-1 border-t border-gray-200">
          <div className="flex flex-col min-w-0">
            {/* MODIFIÉ : Prix avec nouvelles couleurs */}
            <span className="text-sm sm:text-base font-bold text-gray-500 truncate">
              {formatPrice(product.price)} FCFA
            </span>
            {product.discount_price && (
              <div className="flex items-center gap-0.5 mt-0.5">
                <span className="text-[10px] text-gray-400 line-through">
                  {formatPrice(product.discount_price)}
                </span>
                {/* MODIFIÉ : Badge de réduction avec nouvelles couleurs */}
                <span className="text-[9px] bg-yellow-50 text-yellow-600 px-0.5 rounded border border-yellow-100">
                  -{Math.round(((product.price - product.discount_price) / product.price) * 100)}%
                </span>
              </div>
            )}
          </div>
          
          {/* Bouton panier seul avec icone - MODIFIÉ avec nouvelles couleurs */}
          <button
            onClick={handleAddToCart}
            disabled={stockQuantity === 0 || addingToCart}
            className={`p-2 rounded-md transition-all duration-200 ${
              stockQuantity === 0 ? 
                'bg-gray-100 text-gray-400 cursor-not-allowed' :
              addingToCart ? 
                'bg-yellow-100 text-yellow-600 cursor-wait' :
              !isAuthenticated ? 
                'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-600 hover:scale-105' :
                'bg-yellow-400 text-white hover:bg-yellow-500 hover:scale-105'
            }`}
            title={
              stockQuantity === 0 ? "Rupture de stock" :
              addingToCart ? "Ajout en cours..." :
              !isAuthenticated ? "Ajouter au panier (invité)" :
              "Ajouter au panier"
            }
          >
            {addingToCart ? (
              <span className="animate-pulse">...</span>
            ) : (
              <FiShoppingCart className={`text-sm sm:text-base ${
                stockQuantity === 0 ? 'opacity-50' : ''
              }`} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;

// const ProductCard = ({ product, showSupplierInfo = false }) => {
//   const [imageUrl, setImageUrl] = useState('/placeholder.jpg');
//   const [addingToCart, setAddingToCart] = useState(false);
//   const navigate = useNavigate();
//   const { user, isAdmin, isAuthenticated } = useAuth();

//   // Vérification des permissions basée sur user.role.permissions
//   const canViewSupplierNames = () => {
//     // 1. Si showSupplierInfo est false, on n'affiche pas
//     if (!showSupplierInfo) return false;
    
//     // 2. Si l'utilisateur n'est PAS connecté, il ne peut PAS voir
//     if (!isAuthenticated) return false;
    
//     // 3. Si admin, il peut voir
//     if (isAdmin) return true;
    
//     // 4. Vérifier directement dans les permissions de l'utilisateur
//     if (user && user.role && user.role.permissions) {
//       // Vérifier si le tableau de permissions contient "view_suppliers"
//       const hasViewSuppliers = user.role.permissions.includes('view_suppliers');
      
//       return hasViewSuppliers;
//     }
    
//     // 5. Vérifier si le rôle de l'utilisateur lui donne accès (fallback)
//     if (user && user.role) {
//       // Si role est un objet (avec name), vérifier role.name
//       const roleName = typeof user.role === 'object' ? user.role.name : user.role;
      
//       // SEULEMENT les rôles admin et sous_admin peuvent voir
//       const allowedRoles = ['admin', 'sous_admin'];
//       const canView = allowedRoles.includes(roleName);
      
//       return canView;
//     }
    
//     // 6. Par défaut, refuser l'accès
//     return false;
//   };

//   const formatPrice = (price) => {
//     if (!price && price !== 0) return '0';
//     const priceNum = parseFloat(price);
//     if (isNaN(priceNum)) return '0';
    
//     return priceNum.toLocaleString('fr-FR', {
//       minimumFractionDigits: 0,
//       maximumFractionDigits: 0
//     });
//   };

//   const formatSuppliers = (suppliers) => {
//     if (!suppliers || !Array.isArray(suppliers)) return null;
    
//     const canView = canViewSupplierNames();
    
//     if (!isAuthenticated || user?.role?.name === 'user') {
//       // Ne rien afficher du tout si pas de permission
//       return null;
//     }
    
//     const validSuppliers = suppliers.filter(s => s && s.name);
    
//     if (validSuppliers.length === 0) {
//       return null; // Ne rien afficher si pas de fournisseurs
//     }
    
//     // MODIFIÉ : Adapté au nouveau thème jaune/grise
//     return (
//       <div className="text-xs mb-1">
//         <div className="flex items-center gap-1 text-yellow-400">
//           <FiShield className="text-yellow-400 text-xs" />
//           <span className="font-medium">Fournisseurs: {validSuppliers.length}</span>
//         </div>
//       </div>
//     );
//   };

//   const addToLocalCart = (item) => {
//     try {
//       const currentCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
//       const existingIndex = currentCart.findIndex(cartItem => cartItem.product_id === item.product_id);
      
//       if (existingIndex >= 0) {
//         currentCart[existingIndex].quantity += item.quantity;
        
//         if (currentCart[existingIndex].quantity > currentCart[existingIndex].max_quantity) {
//           currentCart[existingIndex].quantity = currentCart[existingIndex].max_quantity;
//           alert(`Quantité maximale atteinte. Stock: ${currentCart[existingIndex].max_quantity}`);
//         }
//       } else {
//         currentCart.push(item);
//       }
      
//       localStorage.setItem('guest_cart', JSON.stringify(currentCart));
//       updateCartBadge(currentCart.reduce((total, item) => total + item.quantity, 0));
      
//       return { success: true, message: 'Produit ajouté au panier local' };
//     } catch (error) {
//       console.error('Erreur lors de l\'ajout au panier local:', error);
//       return { success: false, message: 'Erreur lors de l\'ajout au panier' };
//     }
//   };

//   const addToBackendCart = async (item) => {
//     try {
//       setAddingToCart(true);
      
//       if (!user || !user.id) {
//         return { 
//           success: false, 
//           message: 'Utilisateur non identifié. Veuillez vous reconnecter.' 
//         };
//       }
      
//       const cartData = {
//         user_id: user.id,
//         product_id: item.product_id,
//         quantity: item.quantity,
//       };
      
//       const response = await cartAPI.addToCart(cartData);
      
//       if (response.success) {
//         if (response.data && response.data.total_items) {
//           updateCartBadge(response.data.total_items);
//         }
        
//         return { 
//           success: true, 
//           message: 'Produit ajouté au panier',
//           cartData: response.data
//         };
//       } else {
//         return { 
//           success: false, 
//           message: response.message || 'Erreur lors de l\'ajout au panier' 
//         };
//       }
//     } catch (error) {
//       console.error('Erreur API panier:', error);
      
//       if (error.response) {
//         switch (error.response.status) {
//           case 401:
//             return { 
//               success: false, 
//               message: 'Session expirée. Veuillez vous reconnecter.' 
//             };
//           case 422:
//             return { 
//               success: false, 
//               message: error.response.data.message || 'Données invalides' 
//             };
//           default:
//             return { 
//               success: false, 
//               message: 'Erreur de connexion au serveur' 
//             };
//         }
//       }
      
//       return { 
//         success: false, 
//         message: 'Erreur de connexion au serveur' 
//       };
//     } finally {
//       setAddingToCart(false);
//     }
//   };

//   const updateCartBadge = (itemCount) => {
//     const badgeElement = document.querySelector('.cart-badge');
//     if (badgeElement) {
//       badgeElement.textContent = itemCount > 99 ? '99+' : itemCount;
//       badgeElement.classList.toggle('hidden', itemCount === 0);
//     }
//   };

//   const getImageUrl = (imageName) => {
//     if (!imageName) return null;
    
//     if (imageName.startsWith('http') || imageName.startsWith('blob:')) {
//       return imageName;
//     }
    
//     return `${API_BASE_URL}/storage/products/${imageName}`;
//   };

//   const getPrimaryImage = (images) => {
//     if (!images || images.length === 0) return null;
//     return images.find(img => img.is_primary) || images[0];
//   };

//   const primaryImage = getPrimaryImage(product.image);
//   const imageUrlSoft = primaryImage ? getImageUrl(primaryImage.image) : null;

//   const handleAddToCart = async (e) => {
//     e.stopPropagation();
    
//     const stockQuantity = product.quantity || 10;
//     if (stockQuantity === 0) {
//       alert('❌ Ce produit est en rupture de stock !');
//       return;
//     }
    
//     const cartItem = {
//       product_id: product.id,
//       name: product.name,
//       price: product.price,
//       image: primaryImage?.image,
//       quantity: 1,
//       max_quantity: stockQuantity,
//     };
    
//     if (isAuthenticated && user) {
//       const result = await addToBackendCart(cartItem);
      
//       if (result.success) {
//         console.log(`✅ ${product.name} ajouté au panier !`);
//       } else {
//         alert(`❌ ${result.message}`);
        
//         if (result.message.includes('Session expirée') || result.message.includes('reconnecter')) {
//           setTimeout(() => {
//             navigate('/login', { 
//               state: { 
//                 from: window.location.pathname,
//                 message: 'Session expirée. Veuillez vous reconnecter.'
//               } 
//             });
//           }, 1500);
//         }
//       }
//     } else {
//       const result = addToLocalCart(cartItem);
      
//       if (result.success) {
//         console.log(`✅ ${product.name} ajouté au panier local !`);
//       } else {
//         alert(`❌ ${result.message}`);
//       }
//     }
//   };

//   // const handleViewDetails = () => {
//   //   navigate(`/product/${product.id}`);
//   // };

//   // useEffect(() => {
//   //   fetchProductImage();
//   // }, [product.id]);

//   // const fetchProductImage = async () => {
//   //   try {
//   //     setImageUrl('https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=Produit');
//   //   } catch (error) {
//   //     console.error('Erreur image:', error);
//   //     setImageUrl('https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=Produit');
//   //   }
//   // };

//   const stockQuantity = product.quantity || 10;
//   const getStockStatus = () => {
//     if (stockQuantity > 10) {
//       return { text: `En Stock`, color: 'bg-gray-700 text-yellow-400' };
//     } else if (stockQuantity > 0) {
//       return { text: `Limité`, color: 'bg-yellow-100 text-yellow-800' };
//     } else {
//       return { text: 'Rupture', color: 'bg-green-100 text-green-800' };
//     }
//   };

//   const stockStatus = getStockStatus();

//   // Extrait un aperçu de la description (premières 60 caractères)
//   const getDescriptionPreview = () => {
//     if (!product.description) return 'Aucune description disponible';
    
//     const preview = product.description.substring(0, 60);
//     if (product.description.length > 60) {
//       return preview + '...';
//     }
//     return preview;
//   };

//   return (
//     <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group border border-gray-200 flex flex-col h-full w-full">
//       {/* Image container - Hauteur FIXE pour uniformité */}
//       <Link 
//         to={`/product/${product.id}`}
//         className="relative overflow-hidden cursor-pointer flex-shrink-0 block"
//         // onClick={handleViewDetails}
//       >
//         <LazyLoadImage
//           src={imageUrlSoft || imageUrl}
//           alt={product.name}
//           // effect="blur"
//           className="w-full h-32 sm:h-36 object-cover group-hover:scale-105 transition-transform duration-200"
//           style={{ aspectRatio: '4/3' }}
//           onError={(e) => {
//             e.target.src = 'https://via.placeholder.com/300x200/F59E0B/FFFFFF?text=Produit';
//           }}

//           threshold={100}  // ← Charge l'image 100px avant qu'elle n'apparaisse
//         />
        
//         {/* Badge de stock - MODIFIÉ : Adapté au nouveau thème */}
//         <div className={`absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-semibold ${stockStatus.color} border border-gray-400`}>
//           {stockStatus.text}
//         </div>
//       </Link>

//       {/* Contenu - COMPACT */}
//       <div className="p-2 sm:p-3 flex flex-col flex-grow">
//         {/* Nom du produit - Ligne unique */}
//         <h3 
//           className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-1 mb-1 cursor-pointer hover:text-yellow-400"
//           // onClick={handleViewDetails}
//           // title={product.name}
//         >
//           <Link to={`/product/${product.id}`} title={product.name}>
//             {product.name}
//           </Link>
//         </h3>

//         {/* Aperçu description - Toujours visible */}
//         <div className="mb-1">
//           <p className="text-[10px] sm:text-xs text-gray-500 line-clamp-2">
//             {getDescriptionPreview()}
//           </p>
//           <Link 
//             to={`/product/${product.id}`}
//             className="text-[10px] text-yellow-400 hover:text-yellow-500 font-medium mt-0.5 flex items-center gap-0.5"
//           >
//             Voir détails <FiChevronRight className="text-[9px]" />
//           </Link>
//         </div>

//         {/* Fournisseurs - SEULEMENT si permission */}
//         {formatSuppliers(product.suppliers)}

//         {/* Prix et actions - COMPACT */}
//         <div className="flex items-center justify-between mt-auto pt-1 border-t border-gray-200">
//           <div className="flex flex-col min-w-0">
//             {/* MODIFIÉ : Prix avec nouvelles couleurs */}
//             <span className="text-sm sm:text-base font-bold text-gray-500 truncate">
//               {formatPrice(product.price)} FCFA
//             </span>
//             {product.discount_price && (
//               <div className="flex items-center gap-0.5 mt-0.5">
//                 <span className="text-[10px] text-gray-400 line-through">
//                   {formatPrice(product.discount_price)}
//                 </span>
//                 {/* MODIFIÉ : Badge de réduction avec nouvelles couleurs */}
//                 <span className="text-[9px] bg-yellow-50 text-yellow-600 px-0.5 rounded border border-yellow-100">
//                   -{Math.round(((product.price - product.discount_price) / product.price) * 100)}%
//                 </span>
//               </div>
//             )}
//           </div>
          
//           {/* Bouton panier seul avec icone - MODIFIÉ avec nouvelles couleurs */}
//           <button
//             onClick={handleAddToCart}
//             disabled={stockQuantity === 0 || addingToCart}
//             className={`p-2 rounded-md transition-all duration-200 ${
//               stockQuantity === 0 ? 
//                 'bg-gray-100 text-gray-400 cursor-not-allowed' :
//               addingToCart ? 
//                 'bg-yellow-100 text-yellow-600 cursor-wait' :
//               !isAuthenticated ? 
//                 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-600 hover:scale-105' :
//                 'bg-yellow-400 text-white hover:bg-yellow-500 hover:scale-105'
//             }`}
//             title={
//               stockQuantity === 0 ? "Rupture de stock" :
//               addingToCart ? "Ajout en cours..." :
//               !isAuthenticated ? "Ajouter au panier (invité)" :
//               "Ajouter au panier"
//             }
//           >
//             {addingToCart ? (
//               <span className="animate-pulse">...</span>
//             ) : (
//               <FiShoppingCart className={`text-sm sm:text-base ${
//                 stockQuantity === 0 ? 'opacity-50' : ''
//               }`} />
//             )}
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductCard;